import { KeyboardEvent, useRef } from "react";

import { Tab } from "@/lib/types/Tab";
import { cn } from "@/lib/utils";

import styles from "./Tabs.module.scss";

type TabsVariant = "default" | "rounded";

type TabsProps = {
  tabList: Tab[];
  variant?: TabsVariant;
  ariaLabel?: string;
  dataTest?: string;
};

export const Tabs = ({
  tabList,
  variant = "default",
  ariaLabel,
  dataTest,
}: TabsProps): JSX.Element => {
  const tabRefs = useRef(new Map<string, HTMLButtonElement>());
  const hasPanels = tabList.length > 0 && tabList.every((tab) => tab.panelId);
  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    tabId: string
  ): void => {
    if (
      !hasPanels ||
      !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)
    ) {
      return;
    }
    event.preventDefault();
    const enabledTabs = tabList.filter((tab) => !tab.disabled);
    const currentIndex = enabledTabs.findIndex((tab) => tab.id === tabId);
    const nextIndex =
      event.key === "Home"
        ? 0
        : event.key === "End"
        ? enabledTabs.length - 1
        : (currentIndex +
            (event.key === "ArrowRight" ? 1 : -1) +
            enabledTabs.length) %
          enabledTabs.length;
    const nextTab = enabledTabs[nextIndex];
    if (nextTab) {
      nextTab.onClick();
      tabRefs.current.get(nextTab.id)?.focus();
    }
  };
  const tabsContainerClassName = cn(
    styles.tabs_container,
    variant === "rounded" && styles.rounded
  );

  return (
    <div
      className={tabsContainerClassName}
      role={hasPanels ? "tablist" : undefined}
      aria-label={ariaLabel}
      data-test={dataTest}
    >
      {tabList.map((tab) => (
        <button
          type="button"
          className={cn(
            styles.tab,
            tab.isSelected && styles.selected,
            tab.disabled && styles.disabled
          )}
          key={tab.id}
          id={hasPanels ? tab.id : undefined}
          role={hasPanels ? "tab" : undefined}
          aria-selected={hasPanels ? tab.isSelected : undefined}
          aria-controls={tab.panelId}
          tabIndex={hasPanels ? (tab.isSelected ? 0 : -1) : undefined}
          data-test={tab.dataTest}
          ref={(element) => {
            if (element) {
              tabRefs.current.set(tab.id, element);
            } else {
              tabRefs.current.delete(tab.id);
            }
          }}
          onClick={tab.onClick}
          onKeyDown={(event) => handleKeyDown(event, tab.id)}
          disabled={tab.disabled}
        >
          <span className={styles.label}>{tab.label}</span>
          {!!tab.badge && tab.badge > 0 && (
            <span className={styles.badge}>{tab.badge}</span>
          )}
        </button>
      ))}
    </div>
  );
};
